import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import router from '@/router'
import { WEB_CLIENT_TYPE, classifySessionFailure, getSessionFailureMessage, getWebDeviceId } from '@/utils/auth-session'

// API基础地址配置
const baseURL = process.env.VUE_APP_API_BASE_URL || '/api'

// 当前环境标识（用于调试显示）
const envName = process.env.VUE_APP_ENV_NAME || 'unknown'
console.log(`[API] 当前环境: ${envName}, API地址: ${baseURL}`)

const request = axios.create({
  baseURL: baseURL + '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    if (userStore.accessToken) {
      config.headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
    }
    config.headers['X-Device-Id'] = getWebDeviceId()
    config.headers['X-Client-Type'] = WEB_CLIENT_TYPE
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 判断当前是否在公开页面（收银台等）
const isPublicPage = () => {
  return window.location.pathname.startsWith('/cashier/')
}

let sessionExpiredHandling = false

const handleSessionInvalid = async (reason) => {
  const message = getSessionFailureMessage(reason)

  if (isPublicPage()) {
    ElMessage.error(message)
    return
  }

  if (sessionExpiredHandling) {
    return
  }

  sessionExpiredHandling = true
  try {
    const userStore = useUserStore()
    userStore.clearUser()
    await ElMessageBox.alert(message, reason === 'SESSION_REPLACED' ? '账号已在其他设备登录' : '登录状态已失效', {
      confirmButtonText: '重新登录',
      type: 'warning',
      closeOnClickModal: false,
      closeOnPressEscape: false,
      showClose: false
    })
    router.replace({
      path: '/login',
      query: router.currentRoute.value.path !== '/login' ? { redirect: router.currentRoute.value.fullPath } : {}
    })
  } finally {
    sessionExpiredHandling = false
  }
}

// 标记需要跳转登录
let redirectToLogin = false

// Token刷新锁
let isRefreshing = false
// 待重试的请求队列
const retryRequests = []

// 处理401业务码（令牌过期）
const handleTokenExpired = async () => {
  const userStore = useUserStore()

  // 如果没有refreshToken，直接跳转登录
  if (!userStore.refreshToken) {
    redirectToLogin = true
    return false
  }

  // 如果正在刷新，等待刷新完成
  if (isRefreshing) {
    return new Promise((resolve) => {
      retryRequests.push(resolve)
    })
  }

  isRefreshing = true

  try {
    const success = await userStore.refreshTokenValAndRetry()
    if (success) {
      // 刷新成功，重试所有待重试的请求
      retryRequests.forEach(resolve => resolve(true))
      retryRequests.length = 0
      return true
    } else {
      // 刷新失败，清空队列并跳转登录
      retryRequests.forEach(resolve => resolve(false))
      retryRequests.length = 0
      redirectToLogin = true
      return false
    }
  } finally {
    isRefreshing = false
  }
}

// 响应拦截器
request.interceptors.response.use(
  async (response) => {
    // 如果是 blob 类型响应，直接返回数据
    if (response.config.responseType === 'blob') {
      return response.data
    }

    if (typeof response.data === 'string') {
      return response.data
    }

    const { code, message, data } = response.data

    // 检查业务响应码
    if (code === 401 || code === 1007 || code === 1011) {
      const reason = code === 1011 ? 'SESSION_REPLACED' : 'TOKEN_EXPIRED'
      if (reason === 'SESSION_REPLACED') {
        await handleSessionInvalid(reason)
        return Promise.reject(new Error(getSessionFailureMessage(reason)))
      }
      // 令牌过期，尝试刷新
      const success = await handleTokenExpired()
        if (success) {
          // 刷新成功，重试原请求
          const config = response.config
          config.headers.Authorization = `${useUserStore().tokenType} ${useUserStore().accessToken}`
          return request(config)
        }
        // 刷新失败，清除用户信息并跳转登录
        // 公开页面（如收银台）不跳转登录，直接拒绝请求
        if (isPublicPage()) {
          return Promise.reject(new Error('登录已过期，请重新登录'))
        }
        const userStore = useUserStore()
        userStore.clearUser()
        await handleSessionInvalid(reason)
        return Promise.reject(new Error('登录已过期，请重新登录'))
    }

    if (code === 0) {
      return data
    }

    return Promise.reject(new Error(message || '请求失败'))
  },
  async (error) => {
    const silent = error.config?.silent
    if (error.response) {
      const { status, data } = error.response
      const backendMessage = data?.message || data?.msg
      let fallbackMessage = '请求失败'
      switch (status) {
        case 401: {
          await handleSessionInvalid(classifySessionFailure(error.response))
          break
        }
        case 403:
          fallbackMessage = '没有权限访问'
          if (!silent) ElMessage.error(backendMessage || fallbackMessage)
          break
        case 404:
          fallbackMessage = '请求的资源不存在'
          if (!silent) ElMessage.error(backendMessage || fallbackMessage)
          break
        case 500:
          fallbackMessage = '服务器错误'
          if (!silent) ElMessage.error(backendMessage || fallbackMessage)
          break
        default:
          if (!silent) ElMessage.error(backendMessage || fallbackMessage)
      }
      return Promise.reject(new Error(backendMessage || fallbackMessage))
    } else if (error.code === 'ECONNABORTED') {
      if (!silent) ElMessage.error('请求超时，请稍后重试')
    } else {
      if (!silent) ElMessage.error('网络连接失败，请检查网络')
    }
    return Promise.reject(error)
  }
)

// 检查是否需要跳转登录（供路由守卫调用）
export function checkAndRedirectLogin() {
  if (redirectToLogin) {
    redirectToLogin = false
    return true
  }
  return false
}

// 辅助函数
export function get(url, params = {}, config = {}) {
  return request.get(url, { ...config, params })
}

export function post(url, data) {
  return request.post(url, data)
}

export function put(url, data) {
  return request.put(url, data)
}

export function del(url, data) {
  if (data) {
    return request.delete(url, { data })
  }
  return request.delete(url)
}

export default request
